import React from 'react';
import { Box, Typography } from '@mui/material';
import CustomTeamImageWithDefault from '@/components/shared/custom/CustomTeamImageWithDefault';
import {
    helperEvents,
    translateComment,
} from '@/components/page-components/fixtures/fixture-events/helperEvents';
import CustomSectionWrapper from '@/DesignSystem/wrappers/CustomSectionWrapper';

const PartidoEventos = ({ events, fixture }) => {
    const home = fixture?.home;
    const away = fixture?.away;

    const fixtureEvents = events?.filter((event) => event.team.id === home?.id || event.team.id === away?.id);

    if (!fixtureEvents || fixtureEvents.length === 0 || !fixture) return null;
    // Ordenar eventos por minuto en orden descendente
    const sortedEvents = [...fixtureEvents].sort((a, b) => {
        const timeA = a.time.elapsed + (a.time.extra || 0);
        const timeB = b.time.elapsed + (b.time.extra || 0);
        return timeB - timeA;
    });

    const formatTime = (time) => {
        if (time.extra) {
            return `${time.elapsed}+${time.extra}'`;
        }
        return `${time.elapsed}'`;
    };

    const renderEventContent = (event, isHomeTeam) => {
        const { type, detail, player, assist, comments } = event;
        const isRemoved = false; // Por ahora siempre false, se puede agregar lógica si es necesario

        const mainIcon = helperEvents.getMainEventIcon(type, detail, isRemoved);

        // Para sustituciones, mostrar ambos jugadores pero un solo icono centrado
        if (type.toLowerCase() === 'subst') {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexDirection: isHomeTeam ? 'row' : 'row-reverse',
                    }}
                >
                    {/* Icono de sustitución primero */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>{mainIcon}</Box>

                    {/* Contenedor de nombres */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                        {/* Jugador que entra */}
                        <Typography
                            variant="body2"
                            sx={{ fontSize: '12px', textAlign: isHomeTeam ? 'left' : 'right' }}
                        >
                            {player?.name}
                        </Typography>

                        {/* Jugador que sale */}
                        {assist?.name && (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '11px',
                                    color: 'text.secondary',
                                    textAlign: isHomeTeam ? 'left' : 'right',
                                }}
                            >
                                {assist.name}
                            </Typography>
                        )}

                        {comments && (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '10px',
                                    color: 'text.secondary',
                                    fontStyle: 'italic',
                                    textAlign: isHomeTeam ? 'left' : 'right',
                                }}
                            >
                                {translateComment(comments)}
                            </Typography>
                        )}
                    </Box>
                </Box>
            );
        }

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexDirection: isHomeTeam ? 'row' : 'row-reverse',
                }}
            >
                {/* Icono del evento primero */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>{mainIcon}</Box>

                {/* Contenedor de nombres */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                    {/* Jugador principal */}
                    <Typography
                        variant="body2"
                        sx={{ fontSize: '12px', textAlign: isHomeTeam ? 'left' : 'right' }}
                    >
                        {player?.name}
                    </Typography>

                    {/* Asistencia (solo texto, sin icono) */}
                    {assist?.name && (
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '11px',
                                color: 'text.secondary',
                                textAlign: isHomeTeam ? 'left' : 'right',
                            }}
                        >
                            {assist.name}
                        </Typography>
                    )}

                    {comments && (
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '10px',
                                color: 'text.secondary',
                                fontStyle: 'italic',
                                textAlign: isHomeTeam ? 'left' : 'right',
                            }}
                        >
                            {translateComment(comments)}
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    };

    return (
        <CustomSectionWrapper title="Eventos del partido" mt={2}>
            {/* Header con equipos */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pb: 1,
                    px: 1,
                    backgroundColor: 'background.default',
                    borderBottom: '1px solid',
                    borderColor: 'border.light',
                    borderRadius: 1,
                    height: 40,
                    gap: '2%',
                }}
            >
                {/* Equipo local (derecha) */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '49%',
                        justifyContent: 'flex-start',
                        overflow: 'hidden',
                    }}
                >
                    <CustomTeamImageWithDefault teamId={home.id} width={24} height={24} alt={home.name} />
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: 'calc(100% - 28px)', // espacio para la imagen + gap
                            textAlign: 'right',
                        }}
                    >
                        {home.name}
                    </Typography>
                </Box>

                {/* Equipo visitante (izquierda) */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '49%',
                        justifyContent: 'flex-end',
                        overflow: 'hidden',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: 'calc(100% - 28px)', // espacio para la imagen + gap
                        }}
                    >
                        {away.name}
                    </Typography>
                    <CustomTeamImageWithDefault teamId={away.id} width={24} height={24} alt={away.name} />
                </Box>
            </Box>

            {sortedEvents.map((event, index) => {
                const isHomeTeam = event.team.id === home.id;
                const isLast = index === sortedEvents.length - 1;

                return (
                    <Box
                        key={index}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr auto 1fr',
                            gap: 1,
                            alignItems: 'center',
                            p: 1,
                            borderBottom: isLast ? 'none' : '1px solid',
                            borderColor: 'border.light',
                        }}
                    >
                        {/* Columna local */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                visibility: isHomeTeam ? 'visible' : 'hidden',
                            }}
                        >
                            {renderEventContent(event, isHomeTeam)}
                        </Box>

                        {/* Columna central - tiempo */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    color: 'text.primary',
                                }}
                            >
                                {formatTime(event.time)}
                            </Typography>
                        </Box>

                        {/* Columna visitante */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                visibility: !isHomeTeam ? 'visible' : 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-end',
                                    gap: 0.5,
                                }}
                            >
                                {renderEventContent(event, isHomeTeam)}
                            </Box>
                        </Box>
                    </Box>
                );
            })}
        </CustomSectionWrapper>
    );
};

export default PartidoEventos;
